/**
 * PATH: packages/ui/src/shortcuts/display.ts
 * FILE_ID: UI-SHORTCUTS-DISPLAY-001
 * PURPOSE: Adapter that turns machine-readable ShortcutActions into the
 *          legacy ShortcutHubSection display shape, so the read-only reference
 *          (HubShortcutsReference) is data-driven off the SAME source the
 *          dispatcher uses — without changing its rendered output.
 * WHY: Phase 0 "expand": the general tab is now DERIVED from GLOBAL_ACTIONS
 *      (verified byte-identical to the old hardcoded section in display.test);
 *      un-migrated hubs keep their legacy registry sections until Phase 2.
 *
 * FLOW:
 *   ShortcutAction[] + meta + platform → deriveDisplaySection → ShortcutHubSection
 *   getDisplayRegistry() → legacy registry with 'general' swapped for derived
 *
 * RELATED:
 *   - ./actions.ts: GLOBAL_ACTIONS
 *   - ./binding.ts: formatBinding
 *   - ./registry.ts: SHORTCUT_REGISTRY, ShortcutHubSection
 */

import { formatBinding } from './binding'
import { GLOBAL_ACTIONS } from './actions'
import { SHORTCUT_REGISTRY, type ShortcutEntry, type ShortcutHubSection } from './registry'
import type { DisplayPlatform, ShortcutAction } from './types'

export interface DisplaySectionMeta {
  id: ShortcutHubSection['id']
  label: string
  hint: string
}

/**
 * deriveDisplaySection — build a read-only display section from action
 * descriptors. Uses `displayKeys` when set (doc-only rows), else the formatted
 * default binding. Defaults to 'cross' so output matches the legacy registry.
 * Pure.
 */
export function deriveDisplaySection(
  meta: DisplaySectionMeta,
  actions: ShortcutAction[],
  platform: DisplayPlatform = 'cross',
): ShortcutHubSection {
  const shortcuts: ShortcutEntry[] = actions.map((a) => {
    const keys = a.displayKeys ?? formatBinding(a.defaultBinding, platform)
    const entry: ShortcutEntry = { keys, description: a.description }
    if (a.category) entry.category = a.category
    return entry
  })
  return { id: meta.id, label: meta.label, hint: meta.hint, shortcuts }
}

const GENERAL_META: DisplaySectionMeta = {
  id: 'general',
  label: 'General',
  hint: 'Work everywhere across Quotech',
}

/**
 * getDisplayRegistry — the registry the reference renders. The 'general' tab is
 * derived from GLOBAL_ACTIONS (the action model); every other hub keeps its
 * legacy hardcoded section until it migrates (Phase 2). Output is identical to
 * the pre-refactor SHORTCUT_REGISTRY.
 */
export function getDisplayRegistry(platform: DisplayPlatform = 'cross'): ShortcutHubSection[] {
  const general = deriveDisplaySection(GENERAL_META, GLOBAL_ACTIONS, platform)
  return SHORTCUT_REGISTRY.map((section) => (section.id === 'general' ? general : section))
}
